import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NominalAccount } from '@/types/financials';

interface NominalPickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly nominalAccounts: NominalAccount[];
  readonly loading?: boolean;
  readonly error?: boolean;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
}

export function NominalPicker({
  value,
  displayValue,
  onValueChange,
  nominalAccounts,
  loading = false,
  error = false,
  disabled = false,
  placeholder = 'Select nominal account...',
  className,
}: NominalPickerProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (account: NominalAccount | null) => {
    if (account === null) {
      onValueChange('', '');
    } else {
      onValueChange(account.code, account.name);
    }
    setOpen(false);
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedAccount = nominalAccounts.find(a => a.code === value);
      return selectedAccount
        ? `${selectedAccount.code} - ${selectedAccount.name}`
        : displayValue ?? value;
    }
    return placeholder;
  };

  const getDisabledDisplayValue = (): string => {
    if (!value) return 'None';
    if (displayValue) return `${value} - ${displayValue}`;
    return value;
  };

  if (disabled) {
    return (
      <Input
        value={getDisabledDisplayValue()}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal bg-white"
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search nominal accounts..." />
            <CommandEmpty>
              {error ? 'Failed to load nominal accounts.' : 'No nominal account found.'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              <CommandItem
                key="none"
                value="none"
                onSelect={() => handleSelect(null)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="text-muted-foreground">None</span>
              </CommandItem>
              {nominalAccounts.map((account) => (
                <CommandItem
                  key={account.id}
                  value={`${account.code} ${account.name}`}
                  onSelect={() => handleSelect(account)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === account.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-medium">{account.code}</span>
                  <span className="ml-2 text-muted-foreground">{account.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load nominal accounts</p>
      )}
    </div>
  );
}
